import { MenuItem } from '@mui/material';
import { DateTimePickerAdapter } from 'components/enhanced/form/adapters/date-time/DateTimePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { required } from 'components/helpers/validators';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { accessorService } from 'features/inventory/accessories/api/accessor.service.ts';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractEquipmentService } from 'shared/services';
import { useAppSelector } from 'store/store';
import { Option, StatusOption } from 'shared/types/general';
import { ITicket } from '../../types';
import { ticketingService } from '../../TicketingService';
import { DateTimeTextField } from 'components/cells';
import { SectionHeader } from 'components/ui/typography';

dayjs.extend(customParseFormat);

interface TicketFormParams {
  ticket: ITicket;
  onSubmit: any;
  creation?: boolean;
}
function TicketForm({ ticket, onSubmit, creation = false }: TicketFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.tickets';

  const isDisabledEdit = useIsDirector();
  /** Data/ora editabili solo per amministratori; per i tecnici sono auto-generate */
  const canEditDates = useIsTenantAdministrator();
  const isSuperAdmin: boolean = IsSuperAdminGuard();

  const [equipments, setEquipments] = useState<Option[]>([]);
  const [accessories, setAccessories] = useState<Option[]>([]);
  const [selectedContractEquipment, setSelectedContractEquipment] = useState<Option | null>(null);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const yesNoOptions = ['SI', 'NO'];
  const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);

  useEffect(() => {
    const equipment = ticket?.equipment;
    const contract_params = {
      equipment_status: '1,2',
      ...(equipment ? { equipment_id: equipment, active: true } : {}),
      ...(defaultWarehouse ? { warehouse_code: defaultWarehouse } : {})
    };

    if (equipment && !creation) {
      setEquipments([]);
    } else {
      contractEquipmentService.getAllBaseInformation(contract_params).then((res) => {
        setEquipments(
          res.map((eq) => {
            return {
              id: +eq['id'],
              code: `${eq['description']} (SN ${eq['serial_number']}) - (ID. ${eq['internal_id']})`
            };
          })
        );
      });
    }
    ticketingService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        setStatusOptions(
          res.actions.POST.status.choices.map((item) => {
            return {
              ...item,
              display_name: t(`pages.tickets.status_options.${item.display_name}`)
            };
          })
        );
      }
    });
    accessorService.getAllPaginated(1000, 0).then((res) => {
      setAccessories(res?.results ?? []);
    });
  }, []);

  useEffect(() => {
    const equipmentId = (selectedContractEquipment as any)?.equipment_id;
    if (!equipmentId) {
      accessorService.getAllPaginated(1000, 0).then((res) => {
        setAccessories(res?.results ?? []);
      });
      return;
    }
    accessorService.getAllPaginated(1000, 0, { equipment_id: equipmentId }).then((res) => {
      setAccessories(res?.results ?? []);
    });
  }, [selectedContractEquipment]);

  const statusKey = ticket?.status?.display_name ?? '';
  const isTechnicianResolved = statusKey === 'TECHNICIAN_RESOLVED';
  const isPhoneResolved = statusKey === 'PHONE_RESOLVED';
  const initialYesNoTechnician = isTechnicianResolved ? 'SI' : 'NO';
  const initialYesNoPhone = isPhoneResolved ? 'SI' : 'NO';

  const getStatusOptionValue = (key: string): number | undefined => {
    const opt = statusOptions.find((o) => o.display_name === key);
    return opt?.value;
  };

  /** Parse converte `DD/MM/YYYY HH:mm` (backend) o `YYYY-MM-DD HH:mm` (form) in Dayjs. */
  const parseTicketDateTime = (value?: string | null): Dayjs | null => {
    if (!value) return null;
    const parsed = dayjs(value, ['DD/MM/YYYY HH:mm', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm'], true);
    return parsed.isValid() ? parsed : null;
  };

  const sameMinute = (a: Dayjs | null, b: Dayjs | null): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.startOf('minute').valueOf() === b.startOf('minute').valueOf();
  };

  const formatPayloadDateTime = (d: Dayjs | null): string | null =>
    d && d.isValid() ? d.format('YYYY-MM-DD HH:mm') : null;

  /** Derivazione sincrona dai valori del ticket: nessuna race sugli initialValue. */
  const createdAt = useMemo<Dayjs | null>(() => {
    if (!ticket?.id) return dayjs();
    return parseTicketDateTime(ticket.created_at);
  }, [ticket?.id, ticket?.created_at]);

  const sentAt = useMemo<Dayjs | null>(
    () => parseTicketDateTime(ticket?.sent_at),
    [ticket?.sent_at]
  );

  const specialistArrivalAt = useMemo<Dayjs | null>(
    () => parseTicketDateTime(ticket?.specialist_arrival_at),
    [ticket?.specialist_arrival_at]
  );

  /** Campi editabili fino alla chiusura del ticket (non più disabilitati dopo il solo invio) */
  const isTicketClosed = Boolean(ticket?.resolved_at);
  const isFieldsDisabled = isDisabledEdit || (Boolean(ticket?.id) && isTicketClosed);

  /** Formato data/ora per visualizzazione sola lettura (tecnici) */
  const formatDateTime = (d: Dayjs | null) => {
    if (!d) return '';

    return d.format('DD/MM/YYYY HH:mm');
  };

  return (
    <div>
      {ticket && (
        <EnhancedForm
          key={ticket?.id ?? 'create'}
          onSubmit={(formValues) => {
            // UI SI/NO -> status
            const techYN = String(
              formValues.technician_resolved ?? initialYesNoTechnician
            ).toUpperCase();
            const phoneYN = String(formValues.phone_resolved ?? initialYesNoPhone).toUpperCase();
            const techYes = techYN === 'SI';
            const phoneYes = phoneYN === 'SI';

            if (techYes && !phoneYes) {
              const v = getStatusOptionValue('TECHNICIAN_RESOLVED');
              if (v != null) formValues.status = v;
            } else if (phoneYes && !techYes) {
              const v = getStatusOptionValue('PHONE_RESOLVED');
              if (v != null) formValues.status = v;
            } else if (!techYes && !phoneYes) {
              // se era in uno stato "risolto", riportalo ad OPEN (fallback)
              const current = ticket?.status?.display_name;
              if (current === 'TECHNICIAN_RESOLVED' || current === 'PHONE_RESOLVED') {
                const v = getStatusOptionValue('OPEN');
                if (v != null) formValues.status = v;
              }
            }

            // Date modificabili solo per amministratori: inviare created_at/sent_at
            // SOLO se il valore del form differisce da quello del ticket (confronto al minuto).
            // Mai inviare un `new Date()` di fallback che sovrascrive la data di apertura.
            const formCreatedAt = formValues.created_at ? dayjs(formValues.created_at) : null;
            const formSentAt = formValues.sent_at ? dayjs(formValues.sent_at) : null;
            const formArrivalAt = formValues.specialist_arrival_at
              ? dayjs(formValues.specialist_arrival_at)
              : null;

            if (canEditDates) {
              if (!ticket?.id) {
                formValues.created_at = formatPayloadDateTime(formCreatedAt ?? dayjs());
              } else if (formCreatedAt && !sameMinute(formCreatedAt, createdAt)) {
                formValues.created_at = formatPayloadDateTime(formCreatedAt);
              } else {
                delete formValues.created_at;
              }

              if (!sameMinute(formSentAt, sentAt)) {
                formValues.sent_at = formatPayloadDateTime(formSentAt);
              } else {
                delete formValues.sent_at;
              }
            } else {
              delete formValues.created_at;
              delete formValues.sent_at;
            }

            if (!sameMinute(formArrivalAt, specialistArrivalAt)) {
              formValues.specialist_arrival_at = formatPayloadDateTime(formArrivalAt);
            } else {
              delete formValues.specialist_arrival_at;
            }
            onSubmit(formValues);
          }}
          edit={!!ticket.id}
          hideControls={
            !(
              [
                'OPEN',
                'INTERVENTION_REQUESTED',
                'INTERVENTION_COMPLETED_MISSING_REPORT',
                'INTERVENTION_PENDING',
                'UNDETERMINED'
              ].indexOf(ticket.status.display_name) > -1
            )
          }
          autoStyleLayout={true}
          columns={12}>
          <div style={{ gridColumn: 'span 4' }}>
            {canEditDates ? (
              <IdentityField
                name="created_at"
                component={DateTimePickerAdapter}
                showTime
                label={t('entities.tickets.created_at')}
                initialValue={createdAt}
                type="date"
              />
            ) : (
              <DateTimeTextField
                label={t('entities.tickets.created_at')}
                value={ticket?.created_at ?? (createdAt ? formatDateTime(createdAt) : '')}
                time
                disabled
              />
            )}
          </div>
          {ticket.id && (
            <div style={{ gridColumn: 'span 4' }}>
              {canEditDates ? (
                <IdentityField
                  name="sent_at"
                  component={DateTimePickerAdapter}
                  showTime
                  label={t('entities.tickets.sent_at')}
                  initialValue={sentAt}
                  type="date"
                />
              ) : (
                <DateTimeTextField
                  label={t('entities.tickets.sent_at')}
                  value={ticket?.sent_at ?? (sentAt ? formatDateTime(sentAt) : '')}
                  time
                  disabled
                />
              )}
            </div>
          )}
          {ticket.resolved_at && (
            <div style={{ gridColumn: 'span 4' }}>
              <DateTimeTextField
                label={t(`entities.tickets.resolved_at`)}
                value={ticket.resolved_at}
                time
                disabled
              />
            </div>
          )}
          {ticket.id && !ticket.resolved_at && (
            <IdentityField
              gridSpan={4}
              name="specialist_arrival_at"
              component={DateTimePickerAdapter}
              showTime
              label="Arrivo tecnico specialista (data/ora)"
              initialValue={specialistArrivalAt}
              disabled={isFieldsDisabled}
              type="date"
            />
          )}
          <SectionHeader title={t('pages.supplier.new.registry')} gridSpan />
          {!ticket.id && (
            <>
              <IdentityField
                gridSpan={6}
                name="contract_equipment"
                component={AutocompleteAdapter}
                options={equipments}
                placeholder={t('global.equipment')}
                type="text"
                initialValue={equipments.length === 1 ? equipments[0] : undefined}
                setValue={setSelectedContractEquipment}
              />
              <IdentityField
                gridSpan={6}
                key={`accessor_${selectedContractEquipment?.id ?? 'none'}`}
                name="accessor"
                component={AutocompleteAdapter}
                placeholder={t('global.accessor')}
                options={accessories}
                type="text"
                autoSelect={false}
              />
            </>
          )}

          {ticket.id && ticket.equipment && (
            <IdentityField
              gridSpan={6}
              name="contract_equipment"
              component={TextFieldAdapter}
              initialValue={`${ticket['equipment_description']} (SN ${ticket['serial_number']}) - (ID. ${ticket['internal_id']})`}
              options={equipments}
              placeholder={t('global.equipment')}
              type="text"
              disabled={true}
            />
          )}

          {ticket.id && ticket.accessor && (
            <IdentityField
              gridSpan={6}
              name="accessor"
              component={AutocompleteAdapter}
              placeholder={t('global.accessor')}
              label={t('global.accessor')}
              options={accessories}
              type="text"
              disabled={true}
            />
          )}
          {ticket.id && (
            <IdentityField
              gridSpan={6}
              name="status"
              label={t(`global.status`)}
              initValue={
                ticket.status
                  ? {
                      ...ticket.status,
                      display_name: t(`pages.tickets.status_options.${ticket.status.display_name}`)
                    }
                  : {
                      value: 1,
                      display_name: t(`pages.tickets.status_options.OPEN`)
                    }
              }
              defaultValue={ticket.status ? ticket.status.value : 1}
              component={AutocompleteAdapter}
              options={statusOptions}
              disabled={!isSuperAdmin}
              type="text"
              optionLabel="display_name"
              optionValue="value"
            />
          )}
          <IdentityField
            gridSpan={12}
            name="description"
            initialValue={ticket.description}
            component={TextFieldAdapter}
            label="Problema riscontrato"
            type="text"
            validate={required}
            required
            disabled={isFieldsDisabled}
            multiline
            rows={8}
          />
          <IdentityField
            gridSpan={12}
            name="notes"
            initialValue={ticket.notes ?? ticket.note ?? ''}
            component={TextFieldAdapter}
            label={t('global.notes')}
            type="text"
            disabled={isFieldsDisabled}
            multiline
            rows={3}
          />
          <SectionHeader title={t('pages.tickets.report-info')} gridSpan />
          <IdentityField
            gridSpan={12}
            name="code_error"
            initialValue={ticket.code_error ? ticket.code_error : ''}
            component={TextFieldAdapter}
            label={t('global.code_error')}
            placeholder={t('global.message-placeholder')}
            type="text"
            disabled={isFieldsDisabled}
          />
          {/* STEP 1: Technician Activity */}
          <SectionHeader title={t('pages.tickets.technician')} gridSpan />
          <IdentityField
            gridSpan={2}
            name="technician_resolved"
            initialValue={initialYesNoTechnician}
            component={TextFieldAdapter}
            label="Risolto (SI/NO)"
            disabled={isFieldsDisabled}
            type="text"
            select>
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </IdentityField>
          <IdentityField
            gridSpan={4}
            name="technician_activity"
            initialValue={ticket.technician_activity ? ticket.technician_activity : ''}
            component={TextFieldAdapter}
            label={t('global.activity')}
            disabled={isFieldsDisabled}
            type="text"
          />
          <IdentityField
            gridSpan={6}
            name="technician_activity_description"
            initialValue={
              ticket.technician_activity_description ? ticket.technician_activity_description : ''
            }
            component={TextFieldAdapter}
            label={t(`pages.tickets.activity-description`)}
            disabled={isFieldsDisabled}
            type="text"
            multiline
            rows={4}
          />
          <SectionHeader title={t('pages.tickets.phonecall')} gridSpan />
          <IdentityField
            gridSpan={2}
            name="phone_resolved"
            initialValue={initialYesNoPhone}
            component={TextFieldAdapter}
            label="Risolto (SI/NO)"
            disabled={isFieldsDisabled}
            type="text"
            select>
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </IdentityField>
          <IdentityField
            gridSpan={4}
            name="phonecall_activity"
            initialValue={ticket.phonecall_activity ? ticket.phonecall_activity : ''}
            component={TextFieldAdapter}
            label={t('global.activity')}
            disabled={isFieldsDisabled}
            type="text"
          />
          <IdentityField
            gridSpan={6}
            name="phonecall_activity_description"
            initialValue={
              ticket.phonecall_activity_description ? ticket.phonecall_activity_description : ''
            }
            component={TextFieldAdapter}
            label={t('pages.tickets.activity-description')}
            disabled={isFieldsDisabled}
            type="text"
            multiline
            rows={4}
          />
          <SectionHeader title={t('pages.tickets.spec_field')} gridSpan />
          <IdentityField
            gridSpan={6}
            name="spec_field_activity"
            initialValue={ticket.spec_field_activity ? ticket.spec_field_activity : ''}
            component={TextFieldAdapter}
            label="Richiesto intervento specialista (SI/NO)"
            disabled={isFieldsDisabled}
            type="text"
            select>
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </IdentityField>
          <IdentityField
            gridSpan={6}
            name="spec_field_activity_description"
            initialValue={
              ticket.spec_field_activity_description ? ticket.spec_field_activity_description : ''
            }
            component={TextFieldAdapter}
            label={t('pages.tickets.activity-description')}
            disabled={isFieldsDisabled}
            type="text"
            multiline
            rows={4}
          />
        </EnhancedForm>
      )}
    </div>
  );
}

export default TicketForm;
